"use client";
import { api } from "@/lib/api/api";
import type { External, IResource } from "@gettactic/api";
import { HookFormTextInput } from "@gettactic/components/src/form/TextInput";
import { HookFormUnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput";
import { CrudModal } from "@gettactic/components/src/overlay/modals/CrudModal/CrudModal";
import { CrudModalProps } from "@gettactic/components/src/overlay/modals/CrudModal/CrudModal.types";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useAvigilonExternalIds } from "./useAvigilonExternalIds";
import { Stack } from "@cmp-src/primitives";

export enum AvailableExternalIdTypes {
  resources = "resources",
  office = "office"
}

export const optionsByType = {
  [AvailableExternalIdTypes.resources]: [{ id: "ioffice", name: "iOffice" }],
  [AvailableExternalIdTypes.office]: [
    { id: "avigilon_entry", name: "Avigilon Entry" }
  ]
};

type FormDataShape = {
  name: string;
  external_id: string;
};

type Props = Omit<CrudModalProps, "onOpenChange" | "children"> & {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  resources?: IResource[];
  invalidateQuery: () => Promise<void>;
  object_type?: AvailableExternalIdTypes;
  addedIds?: External[] | null;
};

export function CreateOrEditExternalIDs({
  isOpen,
  onOpenChange,
  resources,
  invalidateQuery,
  object_type = AvailableExternalIdTypes.resources,
  addedIds,
  ...crudModalProps
}: Props) {
  const selectIdFromList = object_type === AvailableExternalIdTypes.office;
  const options = optionsByType[object_type];

  const {
    refetch,
    data: avigilonExternalIds,
    isLoading: isLoadingAvigilon
  } = useAvigilonExternalIds({
    id: resources?.[0]?.id || "",
    object_type,
    enabled: selectIdFromList,
    addedIds: addedIds || null
  });

  // Refetch on open
  useEffect(() => {
    if (isOpen && selectIdFromList) {
      refetch();
    }
  }, [isOpen, selectIdFromList, refetch]);

  const form = useForm<FormDataShape>({
    defaultValues: {
      name: options?.[0]?.id || "",
      external_id: ""
    }
  });

  const avigilonItems =
    avigilonExternalIds && avigilonExternalIds.length > 0
      ? avigilonExternalIds.map((opt) => ({ id: opt.value, name: opt.label }))
      : [{ id: "", name: "No external id found" }];

  const onSubmit = async () => {
    const data = form.getValues();

    if (selectIdFromList && !data.external_id) {
      toast.error("Please select an external id");
      return;
    }

    try {
      await api.client.externals.createExternalId({
        object_type,
        name: data.name,
        object_id: resources?.[0]?.id,
        external_id: data.external_id
      });
      toast.success("External ID created successfully");
      await invalidateQuery();
      onOpenChange(false);
    } catch (e) {
      toast.error("Sorry we couldn't save this id");
    }
  };

  return (
    <CrudModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="New External ID"
      onSubmit={onSubmit}
      submitLabel="Save"
      cancelLabel="Cancel"
      form={form}
      {...crudModalProps}
    >
      <Stack>
        <HookFormUnifiedSelectInput
          form={form}
          name="name"
          label="Name"
          items={options}
          className="w-full lg:w-1/2"
        />

        {selectIdFromList ? (
          <HookFormUnifiedSelectInput
            form={form}
            name="external_id"
            label="External ID"
            items={avigilonItems}
            className="w-full lg:w-1/2"
            isLoading={isLoadingAvigilon}
            searchable
          />
        ) : (
          <HookFormTextInput
            form={form}
            name="external_id"
            label="External ID"
            className="w-full lg:w-1/2"
          />
        )}
      </Stack>
    </CrudModal>
  );
}
